import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FixedWingLine } from '../../features/fixed-wing/models/fixed-wing.models';
import { mergeFixedWingManageLines } from '../../features/fixed-wing/utils/fixed-wing-default-lines';
import { FixedWingState } from './fixed-wing.reducer';

export interface FixedWingDateGroup {
  date: string;
  lines: FixedWingLine[];
}

export const selectFixedWingState = createFeatureSelector<FixedWingState>('fixedWing');

export const selectFixedWingDateRange = createSelector(
  selectFixedWingState,
  (state) => state.dateRange
);

export const selectFixedWingLines = createSelector(selectFixedWingState, (state) => state.lines);

/** Lines grouped by date. Group and line order is exactly the backend response order. */
export const selectFixedWingGroups = createSelector(selectFixedWingLines, (lines) => {
  const byDate = new Map<string, FixedWingLine[]>();
  for (const line of lines) {
    const group = byDate.get(line.date);
    if (group) {
      group.push(line);
    } else {
      byDate.set(line.date, [line]);
    }
  }
  return Array.from(byDate.entries()).map(
    ([date, groupLines]): FixedWingDateGroup => ({ date, lines: groupLines })
  );
});

export const selectFixedWingEligibleCrewByDate = createSelector(
  selectFixedWingState,
  (state) => state.eligibleCrewByDate
);

/** Saved lines of the form date, with unsaved Line 1–Line 6 filled in by order. */
export const selectFixedWingManageLines = createSelector(selectFixedWingState, (state) =>
  state.manageDate ? mergeFixedWingManageLines(state.manageDate, state.savedManageLines) : []
);

/** Active rows only — the Line selector and new assignment. */
export const selectFixedWingActiveManageLines = createSelector(
  selectFixedWingManageLines,
  (lines) => lines.filter((line) => line.active)
);

export const selectFixedWingManageLinesLoading = createSelector(
  selectFixedWingState,
  (state) => state.manageLinesLoading
);

export const selectFixedWingLoading = createSelector(
  selectFixedWingState,
  (state) => state.loading
);

export const selectFixedWingWriting = createSelector(
  selectFixedWingState,
  (state) => state.writing
);

export const selectFixedWingError = createSelector(selectFixedWingState, (state) => state.error);
